<template>
  <section :class="['support-card', { 'support-card--compact': compact }]">
    <div class="support-card__header">
      <div>
        <h3 class="support-card__title">Comentarios del contenido</h3>
      </div>
      <v-chip small class="support-card__chip">
        {{ totalComments }}
      </v-chip>
    </div>

    <ValidationObserver ref="obs" v-slot="{ passes }">
      <div v-if="comments.length === 0 && !loading" class="empty-state">
        <v-icon color="#8a98a5" size="28">mdi-comment-outline</v-icon>
        <span>Este modulo aun no tiene comentarios registrados</span>
      </div>

      <div class="comments-list">
        <article
          v-for="(item, index) in comments"
          :key="index"
          class="comment-item"
        >
          <v-avatar size="32" color="#E9F7FB" class="comment-item__avatar">
            <v-icon color="#0DB2D2" size="18">mdi-account</v-icon>
          </v-avatar>
          <div class="comment-item__content">
            <p class="comment-item__body">{{ item.body }}</p>
            <v-chip
              v-if="item.pending_approval"
              x-small
              class="comment-item__status"
            >
              Pendiente de aprobacion
            </v-chip>
          </div>
        </article>
      </div>

      <div v-if="showPagination" class="comments-pagination">
        <span class="comments-pagination__meta">
          {{ paginationLabel }}
        </span>
        <v-pagination
          :value="currentPage"
          :length="lastPage"
          :disabled="loading"
          total-visible="5"
          color="#0076ff"
          @input="$emit('page-change', $event)"
        />
      </div>

      <div v-if="showForm" class="comment-form">
        <ValidationProvider
          name="comentario"
          rules="required"
          v-slot="{ errors }"
        >
          <v-textarea
            v-model="commentModel"
            outlined
            dense
            auto-grow
            rows="2"
            label="Escribe aqui tu comentario"
            :error-messages="errors"
            :disabled="loading"
            hide-details="auto"
          />
        </ValidationProvider>
      </div>

      <div class="comments-actions">
        <v-btn
          rounded
          depressed
          :outlined="showForm"
          color="#0076ff"
          :dark="!showForm"
          :disabled="loading"
          @click="toggleForm"
        >
          <v-icon left>{{ showForm ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
          {{ showForm ? 'Cancelar' : 'Agregar comentario' }}
        </v-btn>
        <v-btn
          v-if="showForm"
          rounded
          depressed
          color="#061F42"
          dark
          :loading="loading"
          @click="passes(submit)"
        >
          <v-icon left>mdi-send</v-icon>
          Guardar
        </v-btn>
      </div>
    </ValidationObserver>
  </section>
</template>

<script>
export default {
  name: 'ContentComments',
  props: {
    value: {
      type: String,
      default: '',
    },
    showForm: {
      type: Boolean,
      default: false,
    },
    comments: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canLoadMore: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    commentModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    currentPage() {
      return Number(this.pagination.current_page || 1);
    },
    lastPage() {
      return Number(this.pagination.last_page || 1);
    },
    totalComments() {
      return Number(this.pagination.total || this.comments.length || 0);
    },
    showPagination() {
      return this.lastPage > 1;
    },
    paginationLabel() {
      const from = this.pagination.from || (this.comments.length ? 1 : 0);
      const to = this.pagination.to || this.comments.length;
      return `${from}-${to} de ${this.totalComments}`;
    },
  },
  methods: {
    toggleForm() {
      const nextValue = !this.showForm;
      this.$emit('update:show-form', nextValue);
      if (!nextValue) {
        this.$emit('input', '');
        this.resetValidation();
      }
    },
    submit() {
      this.$emit('submit');
    },
    resetValidation() {
      if (this.$refs.obs) {
        this.$refs.obs.reset();
      }
    },
  },
};
</script>

<style scoped>
.support-card {
  width: 100%;
  background: #fff;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  padding: 20px;
}

.support-card--compact {
  border: 0;
  border-radius: 8px;
}

.support-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.support-card__eyebrow {
  margin: 0 0 2px;
  color: #8a98a5;
  font: normal normal 400 12px/18px Poppins;
}

.support-card__title {
  margin: 0;
  color: #101820;
  font: normal normal 700 18px/26px Poppins;
}

.support-card__chip {
  background: #f6f8fa !important;
  color: #607080;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 84px;
  padding: 18px;
  color: #6f7d8a;
  background: #f8fafc;
  border-radius: 8px;
  font: normal normal 400 13px/20px Poppins;
  text-align: center;
}

.comments-list {
  display: grid;
  gap: 10px;
}

.comment-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #edf1f5;
  border-radius: 8px;
}

.comment-item__avatar {
  flex: 0 0 auto;
}

.comment-item__content {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.comment-item__body {
  margin: 0;
  color: #26323d;
  font: normal normal 400 14px/22px Poppins;
  word-break: break-word;
}

.comment-item__status {
  width: fit-content;
  background: #fff7e6 !important;
  color: #946200;
  font: normal normal 500 10px/14px Poppins;
}

.comment-form {
  margin-top: 16px;
}

.comments-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.comments-actions .v-btn {
  text-transform: none;
  font: normal normal 500 13px/22px Poppins;
}

.comments-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.comments-pagination__meta {
  color: #6b7785;
  font: normal normal 400 12px/18px Poppins;
}

.comments-pagination >>> .v-pagination {
  justify-content: flex-end;
}

.comments-pagination >>> .v-pagination__item,
.comments-pagination >>> .v-pagination__navigation {
  min-width: 30px;
  height: 30px;
  box-shadow: none;
}

@media (max-width: 600px) {
  .support-card {
    padding: 16px;
  }

  .comments-actions {
    justify-content: stretch;
  }

  .comments-actions .v-btn {
    flex: 1 1 100%;
  }

  .comments-pagination {
    justify-content: center;
  }
}
</style>
